import React, { useState } from 'react';
import cn from 'classnames';
import styles from './TunnelStoneSelector.module.css';
import { useTranslation } from '@/hooks/useTranslation';
import { CommentStoneOption } from '@/utils/commentStones';
import TunnelStonePickerModal from './TunnelStonePickerModal';

export interface TunnelStoneSelectorProps {
  stoneOptions: CommentStoneOption[];
  selectedStoneId: string;
  onSelect: (stone: CommentStoneOption) => void | Promise<void>;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  disabled?: boolean;
  balance?: number | null;
}

export default function TunnelStoneSelector({
  stoneOptions,
  selectedStoneId,
  onSelect,
  onDragStart,
  onDragEnd,
  disabled = false,
  balance,
}: TunnelStoneSelectorProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const t = useTranslation();

  if (stoneOptions.length === 0) return null;

  const selectedStone =
    stoneOptions.find(stone => stone.id === selectedStoneId) ?? stoneOptions[0];

  const formatPrice = (price: number) =>
    price > 0
      ? `${price.toLocaleString('ru-RU')} $`
      : t('capsuleReactions.freeStone');

  const handleDragStart = (e: React.DragEvent, stoneId: string) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', stoneId);
    onDragStart?.();
  };

  const handleSelect = (stone: CommentStoneOption) => {
    void onSelect(stone);
    setIsPickerOpen(false);
  };

  if (selectedStone) {
    return (
      <>
        <div className={styles.wrapper_selected}>
          <button
            type='button'
            className={styles.stone_preview}
            onClick={() => setIsPickerOpen(true)}
            title={t('capsuleReactions.changeStone')}
            disabled={disabled}
            draggable
            onDragStart={e => handleDragStart(e, selectedStone.id)}
            onDragEnd={onDragEnd}
          >
            <img
              src={selectedStone.imageUrl}
              alt={t('capsuleReactions.selectedStoneAlt')}
              className={styles.stone_preview_img}
              draggable={false}
            />
          </button>
          <span className={cn(styles.selected_name, 'text-p-6')}>
            {selectedStone.name}
          </span>
          <span className={cn(styles.selected_price, 'text-p-7')}>
            {formatPrice(selectedStone.price)}
          </span>
          <span className={cn(styles.change_hint, 'text-p-7')}>
            {t('capsuleReactions.pressToChangeStone')}
          </span>
        </div>

        <TunnelStonePickerModal
          isOpen={isPickerOpen}
          stoneOptions={stoneOptions}
          selectedStoneId={selectedStoneId}
          onSelect={handleSelect}
          onClose={() => setIsPickerOpen(false)}
          disabled={disabled}
          balance={balance}
        />
      </>
    );
  }

  return null;
}
